import fp from "lodash/fp"

import type {
  DevicePlotDef,
  LinePlotView,
  PlotTabsConfig,
  SingleStatPlotView,
  TowerPlotDef,
  WeatherServicePlotDef,
} from "types/dashboard.types"
import type { CustomTooltipProps } from "types/viewProperties.types"

import { darkTheme } from "styles/themes"
import MuiTowerTooltip from "widgets/plots/mui/tower/MuiTowerTooltip"
import { lineView, singleStatView } from "helpers/config/dashboards/base/views"
import {
  OPEN_METEO_ATTRIBUTION,
  linePlotDefBase,
  singleStatPlotDefBase,
} from "helpers/config/dashboards/base/defs"
import { getFieldFormat, segmentField } from "helpers/utils/telemetryFields"
import {
  AGGREGATED_INCLINATION_FIELD,
  TILT_FRONT_FIELD,
  TILT_FRONT_SMOOTHED_FIELD,
  TILT_MAGNITUDE_FIELD,
  TILT_MAGNITUDE_SMOOTHED_FIELD,
  TILT_RIGHT_FIELD,
  TILT_RIGHT_SMOOTHED_FIELD,
  TOWER_BENDING_FIELD,
  TOWER_GLOBAL_LEAN_FRONT_FIELD,
  TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD,
  TOWER_GLOBAL_LEAN_MAGNITUDE_FIELD,
  TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD,
  TOWER_GLOBAL_LEAN_RIGHT_FIELD,
  TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD,
  TOWER_TORSION_FIELD,
  WSERVICE_HUMIDITY_FIELD,
  WSERVICE_PRECIPITATION_FIELD,
  WSERVICE_TEMPERATURE_FIELD,
  WSERVICE_WIND_DIRECTION_FIELD,
  WSERVICE_WIND_GUSTS_FIELD,
  WSERVICE_WIND_SPEED_FIELD,
} from "helpers/config/telemetryFieldDefs"

// ─── Devices inclination in tower (legacy) ────────────────────────────────────

export const devicesInclinationInTowerPlotView: LinePlotView = fp.merge(lineView, {
  name: "Devices inclination",
  nameKey: "DEVICES_INCLINATION",
  helpTooltip: [
    "plots_descriptions.DEVICES_INCLINATION",
    "plots_descriptions.AVERAGE_INCLINATION",
  ],
  properties: {
    axes: {
      y: {
        bounds: ["0", "5"],
        ...getFieldFormat([AGGREGATED_INCLINATION_FIELD]),
      },
    },
    tooltip: {
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiTowerTooltip trigger={trigger} labelType="fieldDevice" />
      ),
    },
  },
})

export const devicesInclinationInTowerPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: devicesInclinationInTowerPlotView,
  dataSource: { entity: "device", resolver: "tower.devices", service: "telemetry" },
  reqConfig: {
    fields: [AGGREGATED_INCLINATION_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

// ─── Torsion ──────────────────────────────────────────────────────────────────

export const torsionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Torsion",
  nameKey: "TORSION",
  helpTooltip: ["plots_descriptions.TOWER_TORSION"],
  properties: {
    fill: ["_field", "from_id", "to_id"],
    axes: {
      y: getFieldFormat([TOWER_TORSION_FIELD]),
    },
    tooltip: {
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiTowerTooltip trigger={trigger} />
      ),
    },
  },
})

export const torsionPlotDef: TowerPlotDef = fp.merge(linePlotDefBase, {
  view: torsionPlotView,
  dataSource: { entity: "tower", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [TOWER_TORSION_FIELD],
    aggregate: ["mean"],
    group: ["tower_id", "_field", "from_id", "to_id"],
    measurement: "tower_health",
  },
})

// ─── Bending ──────────────────────────────────────────────────────────────────

export const bendingPlotView: LinePlotView = fp.merge(lineView, {
  name: "Bending",
  nameKey: "BENDING",
  helpTooltip: ["plots_descriptions.TOWER_BENDING"],
  properties: {
    fill: ["_field", "from_id", "to_id"],
    axes: {
      y: getFieldFormat([TOWER_BENDING_FIELD]),
    },
    tooltip: {
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiTowerTooltip trigger={trigger} />
      ),
    },
  },
})

export const bendingPlotDef: TowerPlotDef = fp.merge(linePlotDefBase, {
  view: bendingPlotView,
  dataSource: { entity: "tower", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [TOWER_BENDING_FIELD],
    aggregate: ["mean"],
    group: ["tower_id", "_field", "from_id", "to_id"],
    measurement: "tower_health",
  },
})

// ─── Weather service wind speed ───────────────────────────────────────────────

export const wServiceWindSpeedPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind speed",
  nameKey: "WEATHER_SERVICE_WIND_SPEED",
  properties: {
    axes: {
      y: getFieldFormat([WSERVICE_WIND_SPEED_FIELD]),
    },
  },
})

export const wServiceWindSpeedPlotDef: WeatherServicePlotDef = fp.merge(linePlotDefBase, {
  view: wServiceWindSpeedPlotView,
  dataSource: { entity: "tower", resolver: "self", service: "weather" },
  attribution: OPEN_METEO_ATTRIBUTION,
  reqConfig: {
    fields: [WSERVICE_WIND_SPEED_FIELD],
    aggregate: ["mean"],
    group: ["_field", "geohash"],
    measurement: "current_weather",
  },
})

const lastWServiceWindSpeedPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last wind speed",
  nameKey: "LAST_WEATHER_SERVICE_WIND_SPEED",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSERVICE_WIND_SPEED_FIELD]),
    size: "lg",
  },
})

export const lastWServiceWindSpeedPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceWindSpeedPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    reqConfig: {
      fields: [WSERVICE_WIND_SPEED_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// ─── Weather service wind gusts ───────────────────────────────────────────────

export const wServiceWindGustPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind gusts",
  nameKey: "WEATHER_SERVICE_WIND_GUSTS",
  properties: {
    axes: {
      y: getFieldFormat([WSERVICE_WIND_GUSTS_FIELD]),
    },
  },
})

export const wServiceWindGustPlotDef: WeatherServicePlotDef = fp.merge(linePlotDefBase, {
  view: wServiceWindGustPlotView,
  dataSource: { entity: "tower", resolver: "self", service: "weather" },
  attribution: OPEN_METEO_ATTRIBUTION,
  reqConfig: {
    fields: [WSERVICE_WIND_GUSTS_FIELD],
    aggregate: ["mean"],
    group: ["_field", "geohash"],
    measurement: "current_weather",
  },
})

const lastWServiceWindGustPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last wind gusts",
  nameKey: "LAST_WEATHER_SERVICE_WIND_GUSTS",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSERVICE_WIND_GUSTS_FIELD]),
    size: "lg",
  },
})

export const lastWServiceWindGustPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceWindGustPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    reqConfig: {
      fields: [WSERVICE_WIND_GUSTS_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// ─── Weather service wind direction ───────────────────────────────────────────

export const wServiceWindDirectionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind direction",
  nameKey: "WEATHER_SERVICE_WIND_DIRECTION",
  helpTooltip: ["plots_descriptions.WEATHER_SERVICE_WIND_DIRECTION"],
  properties: {
    axes: {
      y: getFieldFormat([WSERVICE_WIND_DIRECTION_FIELD]),
    },
  },
})

export const wServiceWindDirectionPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceWindDirectionPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_WIND_DIRECTION_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceWindDirectionPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last wind direction",
  nameKey: "LAST_WEATHER_SERVICE_WIND_DIRECTION",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSERVICE_WIND_DIRECTION_FIELD]),
    size: "lg",
    decimalPlaces: 0,
  },
})

export const lastWServiceWindDirectionPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceWindDirectionPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    reqConfig: {
      fields: [WSERVICE_WIND_DIRECTION_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// ─── Weather service temperature ──────────────────────────────────────────────

export const wServiceTemperaturePlotView: LinePlotView = fp.merge(lineView, {
  name: "Ambient temperature",
  nameKey: "WEATHER_SERVICE_TEMPERATURE",
  properties: {
    axes: {
      y: getFieldFormat([WSERVICE_TEMPERATURE_FIELD]),
    },
  },
})

export const wServiceTemperaturePlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceTemperaturePlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_TEMPERATURE_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceTemperaturePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last temperature",
  nameKey: "LAST_WEATHER_SERVICE_TEMPERATURE",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSERVICE_TEMPERATURE_FIELD]),
    size: "lg",
  },
})

export const lastWServiceTemperaturePlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceTemperaturePlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    reqConfig: {
      fields: [WSERVICE_TEMPERATURE_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// ─── Weather service relative humidity ────────────────────────────────────────

export const wServiceRelativeHumidityPlotView: LinePlotView = fp.merge(lineView, {
  name: "Relative humidity",
  nameKey: "WEATHER_SERVICE_RELATIVE_HUMIDITY",
  properties: {
    axes: {
      y: {
        bounds: ["0", "100"],
        ...getFieldFormat([WSERVICE_HUMIDITY_FIELD]),
      },
    },
  },
})

export const wServiceRelativeHumidityPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceRelativeHumidityPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_HUMIDITY_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceRelativeHumidityPlotView: SingleStatPlotView = fp.merge(
  singleStatView,
  {
    name: "Last relative humidity",
    nameKey: "LAST_WEATHER_SERVICE_RELATIVE_HUMIDITY",
    properties: {
      footerFields: ["_time"],
      ...getFieldFormat([WSERVICE_HUMIDITY_FIELD]),
      size: "lg",
    },
  },
)

export const lastWServiceRelativeHumidityPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceRelativeHumidityPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    reqConfig: {
      fields: [WSERVICE_HUMIDITY_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// ─── Weather service precipitation ────────────────────────────────────────────

export const wServicePrecipitationPlotView: LinePlotView = fp.merge(lineView, {
  name: "Precipitation",
  nameKey: "WEATHER_SERVICE_PRECIPITATION",
  properties: {
    axes: {
      y: {
        bounds: ["0", "10"],
        ...getFieldFormat([WSERVICE_PRECIPITATION_FIELD]),
      },
    },
  },
})

export const wServicePrecipitationPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServicePrecipitationPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_PRECIPITATION_FIELD],
      aggregate: ["max"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServicePrecipitationPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last precipitation",
  nameKey: "LAST_WEATHER_SERVICE_PRECIPITATION",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSERVICE_PRECIPITATION_FIELD]),
    size: "lg",
  },
})

export const lastWServicePrecipitationPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServicePrecipitationPlotView,
    dataSource: { entity: "tower", resolver: "self", service: "weather" },
    reqConfig: {
      fields: [WSERVICE_PRECIPITATION_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// ─── Tower health (vector model) ──────────────────────────────────────────────

// Global lean field constants

export const TOWER_GLOBAL_LEAN_COLORS = {
  front: darkTheme.palette.alpha.main,
  right: darkTheme.palette.beta.main,
  magnitude: darkTheme.palette.extras.limeGreen.main,
} as const

// Reusable instant/smoothed tabsConfig builder
const buildInstantSmoothedTabsConfig = (
  instantFields: string[],
  smoothedFields: string[],
): PlotTabsConfig => ({
  tabs: [
    {
      id: "instant",
      labelKey: "plot_legends.INSTANT",
      tooltipKey: "plot_legends.POSITION_INSTANT_TOOLTIP",
      fields: instantFields,
    },
    {
      id: "smoothed",
      labelKey: "plot_legends.SMOOTHED",
      tooltipKey: "plot_legends.POSITION_SMOOTHED_TOOLTIP",
      fields: smoothedFields,
    },
  ],
  defaultTab: "instant",
})

// ─── Tower tab: global lean (front / right / magnitude combined) ─────────────

const TOWER_GLOBAL_LEAN_FIELDS = [
  TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD,
  TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD,
  TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD,
]

export const towerGlobalLeanPlotView: LinePlotView = fp.merge(lineView, {
  name: "Tower leans",
  nameKey: "TOWER_GLOBAL_LEAN",
  helpTooltip: [
    "plots_descriptions.TOWER_POSITION.FRONT_LEAN",
    "plots_descriptions.TOWER_POSITION.RIGHT_LEAN",
    "plots_descriptions.TOWER_POSITION.LEAN_MAGNITUDE",
    // TEMP: no tabs, so we include the smoothed tab tooltip here
    "plot_legends.POSITION_SMOOTHED_TOOLTIP",
  ],
  properties: {
    axes: {
      y: getFieldFormat(TOWER_GLOBAL_LEAN_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${TOWER_GLOBAL_LEAN_FRONT_FIELD}"}`]: {
        color: TOWER_GLOBAL_LEAN_COLORS.front,
      },
      [`{"_field":"${TOWER_GLOBAL_LEAN_RIGHT_FIELD}"}`]: {
        color: TOWER_GLOBAL_LEAN_COLORS.right,
      },
      [`{"_field":"${TOWER_GLOBAL_LEAN_MAGNITUDE_FIELD}"}`]: {
        color: TOWER_GLOBAL_LEAN_COLORS.magnitude,
      },
      [`{"_field":"${TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD}"}`]: {
        color: TOWER_GLOBAL_LEAN_COLORS.front,
      },
      [`{"_field":"${TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD}"}`]: {
        color: TOWER_GLOBAL_LEAN_COLORS.right,
      },
      [`{"_field":"${TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD}"}`]: {
        color: TOWER_GLOBAL_LEAN_COLORS.magnitude,
      },
    },
    tooltip: {
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiTowerTooltip trigger={trigger} labelType="field" />
      ),
    },
  },
})

export const towerGlobalLeanPlotDef: TowerPlotDef = fp.merge(linePlotDefBase, {
  view: towerGlobalLeanPlotView,
  dataSource: { entity: "tower", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: TOWER_GLOBAL_LEAN_FIELDS,
    aggregate: ["mean"],
    group: ["tower_id", "_field"],
    measurement: "tower_health",
  },
  // TEMP: no instant/smoothed tabs, the plot shows only the .agg fields
  // tabsConfig: buildInstantSmoothedTabsConfig(
  //   [
  //     TOWER_GLOBAL_LEAN_FRONT_FIELD,
  //     TOWER_GLOBAL_LEAN_RIGHT_FIELD,
  //     TOWER_GLOBAL_LEAN_MAGNITUDE_FIELD,
  //   ],
  //   [
  //     TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD,
  //     TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD,
  //     TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD,
  //   ],
  // ),
})

// ─── Devices tab: per-device tilt (3 separate charts) ────────────────────────

const DEVICES_FRONT_TILT_FIELDS = [TILT_FRONT_FIELD, TILT_FRONT_SMOOTHED_FIELD]
const DEVICES_RIGHT_TILT_FIELDS = [TILT_RIGHT_FIELD, TILT_RIGHT_SMOOTHED_FIELD]
const DEVICES_TILT_MAGNITUDE_FIELDS = [
  TILT_MAGNITUDE_FIELD,
  TILT_MAGNITUDE_SMOOTHED_FIELD,
]

const devicesFrontTiltPlotView: LinePlotView = fp.merge(lineView, {
  name: "Devices front tilt",
  nameKey: "TOWER_DEVICES_FRONT_TILT",
  helpTooltip: ["plots_descriptions.TOWER_DEVICES_FRONT_TILT"],
  properties: {
    axes: {
      y: getFieldFormat(DEVICES_FRONT_TILT_FIELDS),
    },
    tooltip: {
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiTowerTooltip trigger={trigger} labelType="fieldDevice" />
      ),
    },
  },
})

export const devicesFrontTiltPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: devicesFrontTiltPlotView,
  dataSource: { entity: "device", resolver: "tower.devices", service: "telemetry" },
  reqConfig: {
    fields: DEVICES_FRONT_TILT_FIELDS,
    aggregate: ["mean"],
    group: ["id", "_field"],
    measurement: "tower_health",
  },
  tabsConfig: buildInstantSmoothedTabsConfig(
    [TILT_FRONT_FIELD],
    [TILT_FRONT_SMOOTHED_FIELD],
  ),
})

const devicesRightTiltPlotView: LinePlotView = fp.merge(lineView, {
  name: "Devices right tilt",
  nameKey: "TOWER_DEVICES_RIGHT_TILT",
  helpTooltip: ["plots_descriptions.TOWER_DEVICES_RIGHT_TILT"],
  properties: {
    axes: {
      y: getFieldFormat(DEVICES_RIGHT_TILT_FIELDS),
    },
    tooltip: {
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiTowerTooltip trigger={trigger} labelType="fieldDevice" />
      ),
    },
  },
})

export const devicesRightTiltPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: devicesRightTiltPlotView,
  dataSource: { entity: "device", resolver: "tower.devices", service: "telemetry" },
  reqConfig: {
    fields: DEVICES_RIGHT_TILT_FIELDS,
    aggregate: ["mean"],
    group: ["id", "_field"],
    measurement: "tower_health",
  },
  tabsConfig: buildInstantSmoothedTabsConfig(
    [TILT_RIGHT_FIELD],
    [TILT_RIGHT_SMOOTHED_FIELD],
  ),
})

const devicesTiltMagnitudePlotView: LinePlotView = fp.merge(lineView, {
  name: "Devices tilt magnitude",
  nameKey: "DEVICES_TILT_MAGNITUDE",
  helpTooltip: ["plots_descriptions.TOWER_DEVICES_TILT_MAGNITUDE"],
  properties: {
    axes: {
      y: {
        bounds: ["0", "5"],
        ...getFieldFormat(DEVICES_TILT_MAGNITUDE_FIELDS),
      },
    },
    tooltip: {
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiTowerTooltip trigger={trigger} labelType="fieldDevice" />
      ),
    },
  },
})

export const devicesTiltMagnitudePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: devicesTiltMagnitudePlotView,
  dataSource: { entity: "device", resolver: "tower.devices", service: "telemetry" },
  reqConfig: {
    fields: DEVICES_TILT_MAGNITUDE_FIELDS,
    aggregate: ["mean"],
    group: ["id", "_field"],
    measurement: "tower_health",
  },
  tabsConfig: buildInstantSmoothedTabsConfig(
    [TILT_MAGNITUDE_FIELD],
    [TILT_MAGNITUDE_SMOOTHED_FIELD],
  ),
})

// ─── Segments tab: dynamic builders (fields injected at runtime from segment list) ───

const SEGMENT_BENDING_NAME_KEYS = {
  longitudinal: "TOWER_SEGMENT_BENDING_LONGITUDINAL",
  lateral: "TOWER_SEGMENT_BENDING_LATERAL",
  magnitude: "TOWER_SEGMENT_BENDING_MAGNITUDE",
} as const

const SEGMENT_BENDING_TOOLTIP_KEYS = {
  longitudinal: "plots_descriptions.TOWER_SEGMENT.BENDING_LONGITUDINAL",
  lateral: "plots_descriptions.TOWER_SEGMENT.BENDING_LATERAL",
  magnitude: "plots_descriptions.TOWER_SEGMENT.BENDING_MAGNITUDE",
} as const

export const buildSegmentBendingPlotDef = (
  component: "longitudinal" | "lateral" | "magnitude",
  segments: string[],
): TowerPlotDef => {
  const smoothedFields = segments.map((s) => segmentField(s, `bending.${component}`))
  return fp.merge(linePlotDefBase, {
    view: fp.merge(lineView, {
      name:
        component === "magnitude"
          ? "Segment bending magnitude"
          : `Segment ${component} bending`,
      nameKey: SEGMENT_BENDING_NAME_KEYS[component],
      helpTooltip: [
        SEGMENT_BENDING_TOOLTIP_KEYS[component],
        ...(component === "magnitude" ? [] : [`img:${component}-bending`]),
        "plot_legends.POSITION_SMOOTHED_TOOLTIP",
      ],
      properties: {
        axes: { y: getFieldFormat(smoothedFields) },
        tooltip: {
          customComponent: ({ trigger }: CustomTooltipProps) => (
            <MuiTowerTooltip trigger={trigger} labelType="field" />
          ),
        },
      },
    }),
    dataSource: { entity: "tower", resolver: "self", service: "telemetry" },
    reqConfig: {
      fields: smoothedFields,
      aggregate: ["mean"],
      group: ["tower_id", "_field"],
      measurement: "tower_health",
    },
  })
}

export const buildSegmentTorsionPlotDef = (segments: string[]): TowerPlotDef => {
  const smoothedFields = segments.map((s) => segmentField(s, "torsion"))
  return fp.merge(linePlotDefBase, {
    view: fp.merge(lineView, {
      name: "Segment torsion",
      nameKey: "TOWER_SEGMENT_TORSION_TOTAL",
      helpTooltip: [
        "plots_descriptions.TOWER_SEGMENT.TORSION",
        "plot_legends.POSITION_SMOOTHED_TOOLTIP",
      ],
      properties: {
        axes: { y: getFieldFormat(smoothedFields) },
        tooltip: {
          customComponent: ({ trigger }: CustomTooltipProps) => (
            <MuiTowerTooltip trigger={trigger} labelType="field" />
          ),
        },
      },
    }),
    dataSource: { entity: "tower", resolver: "self", service: "telemetry" },
    reqConfig: {
      fields: smoothedFields,
      aggregate: ["mean"],
      group: ["tower_id", "_field"],
      measurement: "tower_health",
    },
  })
}
